import { SmartModule, SmartModuleType, TimeSeriesAggregation, TimeSeriesCollectionInfo, TimeSeriesInfo } from '@moderepo/mode-apis';
import { BaseCompAction, BaseCompSubAction, capitalizeAll, capitalizeFirst, SelectInputOption, TimeUnit }
    from '@moderepo/mode-app-components';
import { ritzDataContext, RitzDataState, RitzDataStateAction, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { ExtDispatch, fetchAllHomeSmartModules, fetchAllTimeSeriesCollectionInfo, fetchAllTimeSeriesInfo, fetchEntityClasses,
    selectAllHomeSmartModules, selectAllHomeTimeSeries, selectAllHomeTimeSeriesCollectionInfo, userAppDataContext,
    UserAppDataState, UserAppDataStateAction } from '@moderepo/mode-data-state-user-app';
import { Agent, Entity, EntityClass, EntityMetricsDefinition, MetricInfo, Project, SortOrder } from '@moderepo/mode-internal-apis';
import { showErrorNotification, UIAction } from '@moderepo/mode-ui-state';
import { TFunction } from 'i18next';
import produce, { Draft } from 'immer';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactGridLayout from 'react-grid-layout';
import { MetricsMetadataInputsCompProps, TSDBDashboardWidgetDataSourceInputsCompProps } from '../../components';
import { BaseDashboardWidgetSettingsCompFields, DataDashboardWidgetSettingsCompFields, TSDBDashboardWidgetSettingsFields,
    TSDBDashboardWidgetSettingsCompTexts } from '../../components/dashboard/dashboardCompUtils';
import { useGetAllProjectHomes } from '../../utils/dataStateHooks';
import { FONTAWESOME_ICONS } from '../../utils/fontAwesomeIcons';
import { MATERIAL_UI_ICONS } from '../../utils/materialUIIcons';
import { createHomeOptions, isEntitiesFeatureEnabled, NoneValue, TypeNone } from '../../utils/utils';
import { getTimeSeriesAggregationTypeName } from '../time-series/tsUtils';
import { DashboardLogicApiCallResponse, updateHomeDashboardWidget, updateHomeWidgetsLayouts, updateProjectDashboardWidget,
    updateProjectWidgetsLayouts } from './dashboardLogic';
import { AggregatedDataIntervalType, CollectionDashboardWidgetDataSource, DashboardWidgetLayout, DashboardWidgetSettings,
    DashboardWidgetType, DashboardWidgetViewSettingsInputErrors, DashboardWidgetViewSettingsInputs,
    DataIntervalViewType, isCollectionDashboardWidgetDataSource, isSimpleSeriesDashboardWidgetDataSource, LabelPosition, RawDataIntervalType,
    SimpleSeriesDashboardWidgetDataSource, SingleValueViewType, TSDBDashboardWidgetDataSource, TSDBDashboardWidgetDataSourceInputErrors,
    WidgetDataViewLayout, WidgetGroupWidgetSettings, TSDBDashboardWidgetDataSourceInputs, TSDBDataSourceType,
    EntityTSDBDashboardWidgetDataSource, isEntityDashboardWidgetDataSource, MetricsMetadata, DataDashboardWidgetInputs, DataDashboardWidgetSettings,
    DashboardWidgetInputs, DashboardWidgetInputErrors, DashboardWidgetDataSource } from './dashboardTypes';


/**
 * Below are the functions for returning user friendly display name for some of the value type.
 */


/**
 * User friendly name for Dashboard Widget types which we can display to the users
 */
export const getDashboardWidgetTypeName = (type: DashboardWidgetType): string => {
    const names = Object.freeze({
        [DashboardWidgetType.DATA_INTERVAL]: 'dashboard_widget_type.data_interval',
        [DashboardWidgetType.VALUE]        : 'dashboard_widget_type.value',
        [DashboardWidgetType.HEAT_MAP]     : 'dashboard_widget_type.heat_map',
        [DashboardWidgetType.ALERTS]       : 'dashboard_widget_type.alerts',
        [DashboardWidgetType.ENTITIES_MAP] : 'dashboard_widget_type.entities_map',
        [DashboardWidgetType.WIDGET_GROUP] : 'dashboard_widget_type.widget_group',
    });

    return names[type];
};



/**
 * Get Dashboard Widget type description
 */
export const getDashboardWidgetTypeDescription = (type: DashboardWidgetType): string => {
    const names = Object.freeze({
        [DashboardWidgetType.DATA_INTERVAL]: 'dashboard_widget_type.data_interval.description',
        [DashboardWidgetType.VALUE]        : 'dashboard_widget_type.value.description',
        [DashboardWidgetType.HEAT_MAP]     : 'dashboard_widget_type.heat_map.description',
        [DashboardWidgetType.ALERTS]       : 'dashboard_widget_type.alerts.description',
        [DashboardWidgetType.ENTITIES_MAP] : 'dashboard_widget_type.entities_map.description',
        [DashboardWidgetType.WIDGET_GROUP] : 'dashboard_widget_type.widget_group.description',
    });

    return names[type];
};



/**
 * Get Dashboard Widget icon
 */
export const getDashboardWidgetTypeIcon = (type: DashboardWidgetType): string => {
    const names = Object.freeze({
        [DashboardWidgetType.DATA_INTERVAL]: 'show_chart',
        [DashboardWidgetType.VALUE]        : 'filter_6',
        [DashboardWidgetType.HEAT_MAP]     : 'fas fa-fire',
        [DashboardWidgetType.ALERTS]       : 'notifications_active',
        [DashboardWidgetType.ENTITIES_MAP] : 'public',
        [DashboardWidgetType.WIDGET_GROUP] : 'dashboard',
    });

    return names[type];
};



/**
 * Get Data Interval View Type name
 */
export const getDataIntervalViewTypeName = (type: DataIntervalViewType): string => {
    const names = Object.freeze({
        [DataIntervalViewType.LINE_CHART] : 'data_interval_view_type.line_chart',
        [DataIntervalViewType.BAR_CHART]  : 'data_interval_view_type.bar_chart',
        [DataIntervalViewType.AREA_CHART] : 'data_interval_view_type.area_chart',
        [DataIntervalViewType.GANTT_CHART]: 'data_interval_view_type.gantt_chart',
        [DataIntervalViewType.TABULAR]    : 'data_interval_view_type.tabular',
    });

    return names[type];
};


/**
 * Get Data Interval Type display name
 */
export const getDataIntervalTypeName = (type: AggregatedDataIntervalType | RawDataIntervalType): string => {
    const names = Object.freeze({
        [AggregatedDataIntervalType.LAST_1_DAY]  : 'data_interval_type.last_1_day',
        [AggregatedDataIntervalType.LAST_7_DAYS] : 'data_interval_type.last_7_days',
        [AggregatedDataIntervalType.LAST_30_DAYS]: 'data_interval_type.last_30_days',
        [AggregatedDataIntervalType.LAST_90_DAYS]: 'data_interval_type.last_90_days',
        [AggregatedDataIntervalType.MINUTELY]    : 'data_interval_type.minutely',
        [AggregatedDataIntervalType.HOURLY]      : 'data_interval_type.hourly',
        [AggregatedDataIntervalType.DAILY]       : 'data_interval_type.daily',
        [AggregatedDataIntervalType.WEEKLY]      : 'data_interval_type.weekly',
        [AggregatedDataIntervalType.MONTHLY]     : 'data_interval_type.monthly',
        [RawDataIntervalType.LAST_VALUE]         : 'data_interval_type.last_value',
        [RawDataIntervalType.LAST_50_VALUES]     : 'data_interval_type.last_50_values',
        [RawDataIntervalType.LAST_100_VALUES]    : 'data_interval_type.last_100_values',
        [RawDataIntervalType.LAST_200_VALUES]    : 'data_interval_type.last_200_values',
        [RawDataIntervalType.LAST_300_VALUES]    : 'data_interval_type.last_300_values',
        [RawDataIntervalType.LAST_400_VALUES]    : 'data_interval_type.last_400_values',
        [RawDataIntervalType.LAST_500_VALUES]    : 'data_interval_type.last_500_values',
    });

    return names[type];
};



/**
 * Get Single Value view Type display name
 */
export const getSingleValueViewTypeName = (type: SingleValueViewType): string => {
    const names = Object.freeze({
        [SingleValueViewType.VALUE]: 'single_value_view_type.value',
        [SingleValueViewType.GAUGE]: 'single_value_view_type.gauge',
    });

    return names[type];
};


/**
 * Get Single Value view layout display name
 */
export const getSingleValueViewLayoutName = (type: WidgetDataViewLayout): string => {
    const names = Object.freeze({
        [WidgetDataViewLayout.VERTICAL]  : 'single_value_view_layout.vertical',
        [WidgetDataViewLayout.HORIZONTAL]: 'single_value_view_layout.horizontal',
    });

    return names[type];
};



/**
 * Get Label Position display name
 */
export const getLabelPositionName = (type: LabelPosition): string => {
    const names = Object.freeze({
        top        : 'dashboard.label_position.top',
        bottom     : 'dashboard.label_position.bottom',
        [NoneValue]: 'label.none',
    });

    return names[type];
};


/**
 * Get Single Value view Type display name
 */
export const getTSDBDataSourceTypeName = (type: TSDBDataSourceType): string => {
    const names = Object.freeze({
        [TSDBDataSourceType.SIMPLE]    : 'tsdb_data_source_type.simple',
        [TSDBDataSourceType.COLLECTION]: 'tsdb_data_source_type.collection',
        [TSDBDataSourceType.ENTITY]    : 'tsdb_data_source_type.entity',
    });

    return names[type];
};


/**
 * Get Sort Order name
 */
export const getSortOrderName = (type: SortOrder): string => {
    const names = Object.freeze({
        [SortOrder.ASC] : 'sort_order.asc',
        [SortOrder.DESC]: 'sort_order.desc',
    });

    return names[type];
};



/**
 * Create an Array of Icon options to be used for the icon SELECT input fields.
 * if isOptional is true, we will add a 'None' option to the beginning of the list
 */
export const createIconOptions = (isOptional: boolean = false, noneLabel: string = 'None'): readonly SelectInputOption<string>[] => {
    return [
        ...(isOptional
            ? [{
                value: '',
                label: noneLabel,
                icon : ' ',
            }]
            : []
        ),
        ...MATERIAL_UI_ICONS.map((icon) => {
            return {
                label: icon,
                value: icon,
                icon,
            };
        }),
        ...FONTAWESOME_ICONS.map((icon) => {
            return {
                label: icon,
                value: icon,
                icon,
            };
        }),
    ];
};



/**
 * Create an Array of reload data interval options to be used for the reloadDataFreq SELECT input fields.
 */
export const createReloadDataOptions = (trans: TFunction): readonly SelectInputOption<number>[] => {
    // The options are "1 Minutes", "5 Minutes", "10 Minutes", etc...
    return [{
        value: 0,
        label: capitalizeAll(trans('label.dont_reload_data')),
    },
    ...([1, 5, 10, 30].map((value): SelectInputOption<number> => {
        return {
            value,
            label: capitalizeAll(trans('label.value_unit_display', { value, unit: capitalizeAll(trans(TimeUnit.MINUTE, { count: value })) })),
        };
    })),
    ];
};


/**
 * Create an Array of smart module options to be used for the smart module ID SELECT input field
 */
export const createTSDBDataSourceTypeInputOptions = (
    supportedDataSourceTypes: readonly TSDBDataSourceType[], trans: TFunction,
): readonly SelectInputOption<TSDBDataSourceType>[] => {
    return supportedDataSourceTypes.map((type): SelectInputOption<TSDBDataSourceType> => {
        return {
            value: type,
            label: capitalizeFirst(trans(getTSDBDataSourceTypeName(type))),
        };
    });
};



/**
 * Create an Array of smart module options to be used for the smart module ID SELECT input field
 */
export const createSmartModuleInputOptions = (smartModules: readonly SmartModule[] | undefined): readonly SelectInputOption<string>[] => {
    // This widget type only support TSDB smart modules therefore we need to filter out other type of smart modules
    const supportedSmartModules = smartModules?.filter((module) => {
        return module.moduleType === SmartModuleType.TSDB;
    });

    return supportedSmartModules?.map((module): SelectInputOption<string> => {
        return {
            value: module.id,
            label: module.id,
        };
    }) || [];
};


/**
 * Create an Array of time series options to be used for the seriesIds SELECT input fields.
 */
export const createTimeSeriesOptions = (allTimeSeriesInfo: readonly TimeSeriesInfo[] | undefined): readonly SelectInputOption<string>[] => {
    return allTimeSeriesInfo?.map((seriesInfo): SelectInputOption<string> => {
        return {
            value: seriesInfo.id,
            label: seriesInfo.id,
        };
    }) || [];
};



/**
 * Create an Array of Collection options to be used for the collectionId SELECT input fields.
 */
export const createCollectionOptions = (
    allHomeCollectionInfo: readonly TimeSeriesCollectionInfo[] | undefined,
): readonly SelectInputOption<string> [] => {
    return allHomeCollectionInfo?.map((collectionInfo): SelectInputOption<string> => {
        return {
            value: collectionInfo.id,
            label: collectionInfo.id,
        };
    }) || [];
};


/**
 * Create an Array of Metrics Definition options to be used for the metricsDefinition SELECT input fields.
 */
export const createMetricsDefinitionOptions = (
    allMetricsDefinitions: readonly EntityMetricsDefinition[] | undefined,
): readonly SelectInputOption<EntityMetricsDefinition> [] => {
    return allMetricsDefinitions?.map((definition): SelectInputOption<EntityMetricsDefinition> => {
        return {
            value: definition,
            label: definition.metricsDefinitionId,
        };
    }) || [];
};

/**
 * Create an Array of value names of a Collection to be used for the valueNames SELECT input fields.
 */
export const createCollectionValueNameOptions = (
    allHomeCollections: readonly TimeSeriesCollectionInfo[] | undefined,
    selectedCollectionId: string | undefined,
): readonly SelectInputOption<string> [] => {
    if (allHomeCollections && selectedCollectionId) {
        return allHomeCollections.find((collection) => {
            return collection.id === selectedCollectionId;
        })?.valueNames.map((name): SelectInputOption<string> => {
            return {
                value: name,
                label: name,
            };
        }) || [];
    }

    return [];
};


/**
 * Create an Array of tag names options to be used for the tagNames SELECT input fields.
 */
export const createCollectionTagNameOptions = (
    allHomeCollections: readonly TimeSeriesCollectionInfo[] | undefined,
    selectedCollectionId: string | undefined,
): readonly SelectInputOption<string> [] => {
    if (allHomeCollections && selectedCollectionId) {
        return allHomeCollections.find((collection) => {
            return collection.id === selectedCollectionId;
        })?.tagNames?.map((name): SelectInputOption<string> => {
            return {
                value: name,
                label: name,
            };
        }) || [];
    }

    return [];
};


/**
 * Create an Array of numeric or tag metrics names options to be used for the valueNames and tagNames SELECT input fields.
 */
export const createCollectionMetricNameOptionsFromMetricInfo = (
    metrics: readonly MetricInfo[] | undefined,
): readonly SelectInputOption<string> [] => {
    if (metrics) {
        return metrics.map((metric): SelectInputOption<string> => {
            return {
                value: metric.name,
                label: metric.displayName || metric.name,
            };
        });
    }

    return [];
};

/**
 * Create an Array of Aggregation options to be used for the Aggregation Type SELECT input fields. These are the 'Average', 'Min', 'Sum', etc...
 * options
 */
export const createAggregationTypeOptions = (
    trans: TFunction, inputs: TSDBDashboardWidgetDataSourceInputs,
): readonly SelectInputOption<TimeSeriesAggregation | TypeNone> [] => {
    return [...Object.values(TimeSeriesAggregation).map((type): SelectInputOption<TimeSeriesAggregation> => {
        return {
            value   : type,
            label   : capitalizeFirst(trans(getTimeSeriesAggregationTypeName(type))),
            // If at least 1 'tagName' is selected, we can't fetch AGGREGATED data so we need to disable all these options
            disabled: (inputs.dataSourceType === TSDBDataSourceType.COLLECTION || inputs.dataSourceType === TSDBDataSourceType.ENTITY)
                && inputs.tagNames && inputs.tagNames.length > 0,
        };
    }), {
        value: NoneValue,
        label: capitalizeAll(trans('label.none')),
    }];
};



/**
 * Create an Array of Interval Type options to be used for the Interval Type SELECT input fields. These are the 'Last 1 day', 'Last 7 Day',
 * 'Hourly', 'Weekly', etc... options
 */
export const createIntervalTypeOptions = (
    types: readonly (AggregatedDataIntervalType | RawDataIntervalType)[],
    trans: TFunction, inputs: TSDBDashboardWidgetDataSourceInputs,
): readonly SelectInputOption<AggregatedDataIntervalType | RawDataIntervalType> [] => {

    // Combine AggregatedDataIntervalType and RawDataIntervalType options
    return types.map((type): SelectInputOption<AggregatedDataIntervalType | RawDataIntervalType> => {
        // AggregatedIntervalTypes can only be used when aggregationType is set to 1 of the aggregation types.
        // And RawDataIntervalType can only be used when aggregationType is set to None
        const isAggregatedIntervalType = Object.values(AggregatedDataIntervalType).includes(type as AggregatedDataIntervalType);

        const disabled = (inputs.aggregationType !== NoneValue && !isAggregatedIntervalType)
                            || (inputs.aggregationType === NoneValue && isAggregatedIntervalType);
        return {
            value: type,
            label: capitalizeFirst(trans(getDataIntervalTypeName(type))),
            disabled,
        };
    });
};



/**
 * Create an Array of View layout options to be used for the View Layout SELECT input fields. These are the 'Vertical', 'Horizontal' options.
 */
export const createWidgetDataViewLayoutOptions = (
    trans: TFunction,
): readonly SelectInputOption<WidgetDataViewLayout> [] => {
    return Object.values(WidgetDataViewLayout).map((type): SelectInputOption<WidgetDataViewLayout> => {

        return {
            value: type,
            label: capitalizeFirst(trans(getSingleValueViewLayoutName(type))),
        };
    });
};



/**
 * Helper function that create a dataSource object based on the inputs. We use this in a couple of places, createWidget and updateWidget
 * therefore we will create this function to make it easier.
 */
export const createDataSourceSettingsFromInputs = (
    inputs: TSDBDashboardWidgetDataSourceInputs,
): SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource | EntityTSDBDashboardWidgetDataSource => {
    if (inputs.dataSourceType === TSDBDataSourceType.SIMPLE) {
        return {
            homeId         : inputs.homeId,
            dataSourceType : inputs.dataSourceType,
            smartModuleType: SmartModuleType.TSDB,
            smartModuleId  : inputs.smartModuleId,
            aggregationType: inputs.aggregationType,
            intervalType   : inputs.intervalType,
            seriesIds      : inputs.seriesIds,
            metricsMetadata: inputs.metricsMetadata || {},
        } as SimpleSeriesDashboardWidgetDataSource;
    }
    if (inputs.dataSourceType === TSDBDataSourceType.COLLECTION) {
        return {
            homeId         : inputs.homeId,
            dataSourceType : inputs.dataSourceType,
            smartModuleType: SmartModuleType.TSDB,
            smartModuleId  : inputs.smartModuleId,
            aggregationType: inputs.aggregationType,
            intervalType   : inputs.intervalType,
            collectionId   : inputs.collectionId,
            valueNames     : inputs.valueNames,
            tagNames       : inputs.tagNames,
            metricsMetadata: inputs.metricsMetadata || {},
        } as CollectionDashboardWidgetDataSource;
    }
    return {
        homeId             : inputs.homeId,
        dataSourceType     : inputs.dataSourceType,
        smartModuleType    : SmartModuleType.TSDB,
        entityId           : inputs.entity?.entityId,
        metricsDefinitionId: inputs.metricsDefinition?.metricsDefinitionId,
        aggregationType    : inputs.aggregationType,
        intervalType       : inputs.intervalType,
        valueNames         : inputs.valueNames,
        tagNames           : inputs.tagNames,
        metricsMetadata    : inputs.metricsMetadata || {},
    } as EntityTSDBDashboardWidgetDataSource;
};



/**
 * Given the name of the field from the data source inputs that was updated and the new value, return a new TSDBDashboardWidgetDataSourceInputs
 * that contain all the input values. This looks like a straightforward thing to do but it is a little more complicated. Lets say the
 * data source's "intervalType" is changed so we should just update the currentInputs.intervalType like this
 *
 *      // Return a copy of the current inputs and then replace the intervalType with the new value
 *      return {
 *          ...currentInputs,
 *          intervalType: fieldValue
 *      }
 *
 * However, some field's changes affect other fields. For example, 'smartModuleId'. When the user select a different 'smartModuleId', many of the
 * inputs from the currentInputs are invalid e.g. seriesIds, collectionIds, tagNames, valueNames, etc... So it is a little bit more complicated to
 * create a new Data source inputs based on the field that changed.
 *
 * @param fieldName
 * @param fieldValue
 * @param currentInputs
 * @returns
 */
export const getUpdateDashboardWidgetDataSourceInputs = (
    fieldName: keyof TSDBDashboardWidgetDataSourceInputsCompProps['fields'],
    fieldValue: any,
    currentInputs: TSDBDashboardWidgetDataSourceInputs & DashboardWidgetViewSettingsInputs,
): TSDBDashboardWidgetDataSourceInputs & DashboardWidgetViewSettingsInputs => {

    const result = produce(currentInputs, (draft: Draft<TSDBDashboardWidgetDataSourceInputs & DashboardWidgetViewSettingsInputs>) => {

        // Update the value for the field that changed
        draft[fieldName] = fieldValue;

        // If 'homeId' or 'dataSourceType' changed then almost all other inputs has to be cleared
        if (fieldName === 'homeId' || fieldName === 'dataSourceType') {
            draft.smartModuleId = undefined;
            draft.seriesIds = undefined;
            draft.collectionId = undefined;
            draft.entity = undefined;
            draft.metricsDefinition = undefined;
            draft.valueNames = undefined;
            draft.tagNames = undefined;
            draft.metricsMetadata = {};
            draft.viewMetricsMetadata = {};
        }

        // If smartModuleId changed then almost all other inputs has to be cleared
        if (fieldName === 'smartModuleId') {
            draft.seriesIds = undefined;
            draft.collectionId = undefined;
            draft.valueNames = undefined;
            draft.tagNames = undefined;
            draft.metricsMetadata = {};
            draft.viewMetricsMetadata = {};
        }

        // If `collectionId` changed then valueNames and tagNames have to be cleared because the new collection will have a different
        // set of valueNames and tagNames. And if valueNames and tagNames are cleared, the metricsMetadata has to be cleared as well because
        // it still has the previous selected metrics
        if (fieldName === 'collectionId') {
            draft.valueNames = undefined;
            draft.tagNames = undefined;
            draft.metricsMetadata = {};
            draft.viewMetricsMetadata = {};
        }

        // If 'entity' changed then the user need to reselect metricsDefinition which mean valueNames, tagNames, etc... also need to be changed
        if (fieldName === 'entity') {
            draft.metricsDefinition = undefined;
            draft.valueNames = undefined;
            draft.tagNames = undefined;
            draft.metricsMetadata = {};
            draft.viewMetricsMetadata = {};
        }

        // If 'metricsDefinition' changed then the user need to reselect different valueNames, tagNames, etc...
        if (fieldName === 'metricsDefinition') {
            draft.valueNames = undefined;
            draft.tagNames = undefined;
            draft.metricsMetadata = {};
            draft.viewMetricsMetadata = {};
        }

        /**
         * If the user updated the seriesIds, valueNames, or tagNames array, we need to update the metricsMetadata accordingly
         * We will add/remove items from the metricsMetadata accordingly based on whether the user added/removed seriesId, valueName, or tagName
         *
         * E.g. Lets say this is the currentMetricsMetadata
         *      {
         *          temperature: {min:0, max:10}
         *      }
         *
         * And the user added "pressure" metric then this will be the newMetricsMetadata
         *      {
         *          temperature: {min:0, max:10},
         *          pressure: {}
         *      }
         * */
        if (fieldName === 'seriesIds' || fieldName === 'valueNames' || fieldName === 'tagNames') {

            // get the current list of metric names
            const currentMetricNames = currentInputs[fieldName];

            // Get the new list of new metric. We can assume 'value' will be an array of series ids, array of valueNames, or array of tagNames
            const newMetricNames = (fieldValue as string[]);

            // We know the metric names list changed but we don't know if the user added or remove metrics. So we need to compare the 2 lists
            // of names to find the metric name/names that was added or removed.

            // create a newMetricsMetadata by copying the current metrics metadata, currentUserInputs.metricsMetadata
            const newMetricsMetadata = {
                ...currentInputs.metricsMetadata,
            };

            // Go through each metric name in the newMetricNames list. If a name DOES NOT exist in the currentMetricNames, we know the user
            // must have added that metric name therefore we can add it to the newMetricsMetadata
            newMetricNames.forEach((metricName) => {
                if (!currentMetricNames?.includes(metricName)) {
                    // Add a new metric name to the newMetricsMetadata
                    newMetricsMetadata[metricName] = {};
                }
            });

            // Go through each name from currentMetricNames list. If a name DOES NOT exist in the newMetricNames list, we know the user
            // must have removed it from the list. We can remove it from the newMetricsMetadata
            currentMetricNames?.forEach((metricName) => {
                if (!newMetricNames?.includes(metricName)) {
                    delete newMetricsMetadata[metricName];
                }
            });


            // Do the same for viewMetricsMetadata. Whenever the user added/removed a metric, we need to update the viewMetricsMetadata
            // create a newViewMetricsMetadata by copying the current metrics metadata, currentUserInputs.viewMetricsMetadata
            const newViewMetricsMetadata = {
                ...currentInputs.viewMetricsMetadata,
            };

            // Go through each metric name in the newMetricNames list. If a name DOES NOT exist in the currentMetricNames, we know the user
            // must have added that metric name therefore we can add it to the newMetricsMetadata
            newMetricNames.forEach((metricName) => {
                if (!currentMetricNames?.includes(metricName)) {
                    // Add a new metric name to the newMetricsMetadata
                    newViewMetricsMetadata[metricName] = {};
                }
            });

            // Go through each name from currentMetricNames list. If a name DOES NOT exist in the newMetricNames list, we know the user
            // must have removed it from the list. We can remove it from the newMetricsMetadata
            currentMetricNames?.forEach((metricName) => {
                if (!newMetricNames?.includes(metricName)) {
                    delete newViewMetricsMetadata[metricName];
                }
            });


            // Now replace the metricsMetadata with the new newMetricsMetadata object
            draft.metricsMetadata = newMetricsMetadata;
            draft.viewMetricsMetadata = newViewMetricsMetadata;
        }
    });

    return result;
};



/**
 * Custom hook that get the data sources e.g. Smart Modules, TimeSeries, Collections, from the data state based on the current user inputs.
 * This will also make API calls to fetch these data if the data are not fetched.
 * We use these in all the Widget settings container therefore we will create a Custom Hook so that we can reuse these for all the widget.
 *
 * @param currentUserInputs
 * @returns
 */
export const useGetWidgetDataSources = (
    currentUserInputs: TSDBDashboardWidgetDataSourceInputs, projectId: number, entityClasses: readonly EntityClass[] | undefined,
): {
    readonly smartModules: readonly SmartModule[] | undefined;
    readonly allHomeTimeSeries: readonly TimeSeriesInfo[] | undefined;
    readonly allHomeCollections: readonly TimeSeriesCollectionInfo[] | undefined;
    readonly allMetricsDefinitions: readonly EntityMetricsDefinition[] | undefined;
} => {
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    // Select all the home's smartModules from the data state and cache them if homeId is selected.
    const smartModules = useMemo(() => {
        if (currentUserInputs.homeId) {
            return selectAllHomeSmartModules(appDataState, currentUserInputs.homeId);
        }
        return undefined;
    }, [appDataState, currentUserInputs.homeId]);


    // Select all home simple time series from the data state and cache them if homeId is selected and the selected dataSourceType is SIMPLE
    const allHomeTimeSeries = useMemo(() => {
        if (currentUserInputs.homeId && currentUserInputs.dataSourceType === TSDBDataSourceType.SIMPLE && currentUserInputs.smartModuleId) {
            return selectAllHomeTimeSeries(appDataState, currentUserInputs.homeId, currentUserInputs.smartModuleId);
        }
        return undefined;
    }, [appDataState, currentUserInputs.homeId, currentUserInputs.smartModuleId, currentUserInputs.dataSourceType]);


    // Select all home Collections from the data state and cache them if homeId is selected and the selected dataSourceType is COLLECTION
    const allHomeCollections = useMemo(() => {
        if (currentUserInputs.homeId && currentUserInputs.dataSourceType === TSDBDataSourceType.COLLECTION && currentUserInputs.smartModuleId) {
            return selectAllHomeTimeSeriesCollectionInfo(appDataState, currentUserInputs.homeId, currentUserInputs.smartModuleId);
        }
        return undefined;
    }, [appDataState, currentUserInputs.homeId, currentUserInputs.smartModuleId, currentUserInputs.dataSourceType]);


    // Select all metricsDefinition from the data state and cache them if an entity is selected
    const allMetricsDefinitions = useMemo(() => {
        if (currentUserInputs.entity && currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY) {
            const entityClass = entityClasses?.find((entityClass) => {
                return entityClass.entityClass === currentUserInputs.entity?.entityClass;
            });
            if (entityClass) {
                // EntityClass has an array of metricsDefinitions but we only want the metricsDefinitions of type TSDB.
                return entityClass.metricsDefinitions?.filter((definition) => {
                    return definition.dataStoreConfiguration.moduleType === SmartModuleType.TSDB;
                });
            }
        }
        return undefined;
    }, [currentUserInputs.dataSourceType, currentUserInputs.entity, entityClasses]);


    /**
     * This useEffect is used for fetching all the smart modules created for the provided homeId. We will show this list of smart module for
     * the user to choose
     */
    useEffect(() => {
        if (!smartModules && currentUserInputs.homeId) {
            appDataDispatch(fetchAllHomeSmartModules(currentUserInputs.homeId)).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        }
    }, [appDataDispatch, currentUserInputs.homeId, smartModules]);


    /**
     * This use effect is used for fetching home's list of time series based on the user's selected smart module id.
     * We don't need to fetch time series info if the user selected a different time series type
     */
    useEffect(() => {
        // If we have not fetched time series info for the selected home yet and the user has already selected a TSDBDataSourceType.SIMPLE
        // as time series type and smartModuleId, we can fetch it now
        if (!allHomeTimeSeries && currentUserInputs.homeId && currentUserInputs.dataSourceType === TSDBDataSourceType.SIMPLE
            && currentUserInputs.smartModuleId
        ) {
            appDataDispatch(fetchAllTimeSeriesInfo(currentUserInputs.homeId, currentUserInputs.smartModuleId)).catch(() => {
                // ignore error
            });
        }
    }, [appDataDispatch, currentUserInputs.smartModuleId, currentUserInputs.dataSourceType, allHomeTimeSeries, currentUserInputs.homeId]);


    /**
     * This use effect is used for fetching home's list of Collections based on the user's selected smart module id.
     * We don't need to fetch collections info if the user selected a different time series type
     */
    useEffect(() => {
        // If we have not fetched time series info for the selected home yet and the user has already selected a TSDBDataSourceType.COLLECTION
        // as time series type and smartModuleId, we can fetch it now
        if (!allHomeCollections && currentUserInputs.homeId && currentUserInputs.dataSourceType === TSDBDataSourceType.COLLECTION
            && currentUserInputs.smartModuleId
        ) {
            appDataDispatch(fetchAllTimeSeriesCollectionInfo(currentUserInputs.homeId, currentUserInputs.smartModuleId)).catch(() => {
                // ignore error
            });
        }
    }, [appDataDispatch, currentUserInputs.smartModuleId, currentUserInputs.dataSourceType, currentUserInputs.homeId, allHomeCollections]);


    /**
     * This useEffect is used for fetching all EntityClasses if an entity is selected but we are not able to find metrics definition
     */
    useEffect(() => {
        if (!allMetricsDefinitions && currentUserInputs.entity && currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY) {
            appDataDispatch(fetchEntityClasses(projectId));
        }
    }, [allMetricsDefinitions, appDataDispatch, currentUserInputs.dataSourceType, currentUserInputs.entity, projectId]);


    return {
        smartModules, allHomeTimeSeries, allHomeCollections, allMetricsDefinitions,
    };
};


/**
 * Custom hook to get the list of supported data source types. The list of possible data source types are defined in enum TSDBDataSourceType.
 * However, it is possible that one of them are not supported maybe because the feature is not enabled. Therefore, we need
 * to filter out the one not supported instead of showing all the possible types
 */
export const useGetSupportedDataSourceTypes = (project: Project | undefined, loggedInAgent: Agent | undefined) => {
    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, loggedInAgent);

    return Object.values(TSDBDataSourceType).filter((type) => {
        // if type is 'Entities', make sure the project's uiSettings has this flag set to true
        return type !== TSDBDataSourceType.ENTITY || entityFeatureEnabled;
    });
};


/**
 * One problem with selectedWidget object is that it changes every time we refetch dashboard settings. Most of the time, the widget's
 * internal content is the same, the reference to the object changed therefore it triggers re-render. Also, sometime a widget's settings changed
 * BUT only the title, icon, or other changes NOT related to dataSource which also cause re-rendering. So to fix this issue, we will make
 * local copy of the selectedWidget.dataSource and only update this local copy of this widget's dataSource when the dataSource changed
 * To determine if the dataSource of the selectedWidget changed and not just the reference to the object, we compare
 * the STRINGIFIED representation of the selectedWidget.dataSource and the local dataSource. If the 2 objects have the same string
 * representation, we can assume the objects are the same and don't update the local copy.
 *
 * We need this in many places therefore we will create a custom hook so we can reuse it.
 */
export const useWidgetDataSource = <T extends DashboardWidgetDataSource | undefined>(newDataSource: T): T => {
    const [dataSource, setDataSource] = useState(newDataSource);

    // Convert the dataSource to string and cache it so that we don't have to do this every time we need to compare the dataSource
    const stringifiedDataSource = useMemo(() => {
        return JSON.stringify(dataSource);
    }, [dataSource]);

    useEffect(() => {
        if (JSON.stringify(newDataSource) !== stringifiedDataSource) {
            setDataSource(newDataSource);
        }
    }, [newDataSource, stringifiedDataSource]);

    return dataSource;
};



/**
 * A function that create the input fields for the Data Source settings part of the Widget settings form. This part of the settings is the
 * same for all widget therefore we will put them into this function so that we can reuse it for all the widgets
 *
 * @param params
 * @returns
 */
export const createViewMetricMetadataInputField = (params: {
    readonly currentUserInputs: TSDBDashboardWidgetDataSourceInputs & DashboardWidgetViewSettingsInputs;
    readonly inputErrors: DashboardWidgetViewSettingsInputErrors;
    readonly hidden: boolean,
    readonly contextHomeId: number | undefined;
    readonly trans: TFunction;
}): MetricsMetadataInputsCompProps['fields'] => {

    const metricsSelected = (params.currentUserInputs.seriesIds && params.currentUserInputs.seriesIds.length > 0)
            || (params.currentUserInputs.valueNames && params.currentUserInputs.valueNames.length > 0)
            || (params.currentUserInputs.tagNames && params.currentUserInputs.tagNames.length > 0);

    return {
        metricsMetadata: {
            label   : 'metadata',
            value   : params.currentUserInputs.viewMetricsMetadata,
            editable: true,
            hidden  : params.hidden || !metricsSelected,
            variant : 'outlined',
            error   : params.inputErrors.viewMetricsMetadata
                ? JSON.stringify(Object.keys(params.inputErrors.viewMetricsMetadata).reduce((result, metricName) => {
                    // translate the error message for each metric
                    const metricErrorMessage = params.inputErrors.viewMetricsMetadata?.[metricName];
                    if (metricErrorMessage) {
                        return {
                            ...result,
                            [metricName]: params.trans(metricErrorMessage),
                        };
                    }
                    return result;
                }, {}))                 // 'error' is a string therefore we need to JSON.stringify the metricMetadata error object
                : undefined,
        },
    };
};



/**
 * Create the layouts object for all the widgets based on the widgets' position and sizes. The React Grid Layout needs this object
 * to layout the widgets.
 */
export const getWidgetsLayout = (
    widgets: readonly DashboardWidgetSettings[] | undefined,
    minWidth: number,
    minHeight: number,
): ReactGridLayout.Layouts => {
    if (widgets) {
        return widgets.reduce((
            result: ReactGridLayout.Layouts,
            widget: DashboardWidgetSettings,
        ): ReactGridLayout.Layouts => {
            const newResult = { ...result };
            if (widget.layouts) {
                Object.keys(widget.layouts).forEach((screenSize: string) => {
                    const widgetLayout = {
                        i   : widget.id.toString(),
                        x   : widget.layouts[screenSize].x ?? Infinity,    // If the widget does not have a position value, use value Infinity.
                        y   : widget.layouts[screenSize].y ?? Infinity,    // The react-grid-layout will place them at the end of the container
                        w   : widget.layouts[screenSize].w,
                        h   : widget.layouts[screenSize].h,
                        minW: minWidth,
                        minH: minHeight,
                    };

                    if (!newResult[screenSize]) {
                        // If we don't have the layout settings for the specified screenSize yet, create a new one
                        newResult[screenSize] = [];
                    }
                    newResult[screenSize].push(widgetLayout);
                });
            }
            return newResult;
        }, {});
    }

    return {};
};



/**
 * Helper function to handle the event when a widget is resized or moved inside a Grid Layout
 * On a widget resize or moved, we will get the updated layout of the widgets and save them in Home KV
 * The 'layouts' will be an array of layout object, 1 for each widget. We need to convert it to something else to make it easier to
 * use
 * This is the structure of 'layouts'
 *      [
 *          {i:'widget1', x:0, y:0, w:1, h:2},
 *          {i:'widget2', x:1, y:2, w:3, h:1},
 *          ...
 *      ]
 *
 * We want to organize them by widgetId to make it easier to use. So we will reformatted 'layouts' to this format
 *      {
 *          widget1: {
 *              lg: {x:0, y:0, w:1, h:2}
 *          },
 *          widget2: {
 *              lg: {x:1, y:2, w:3, h:1}
 *          }
 *      }
 */
export const handleWidgetResizedOrMoved = (params: {
    readonly layouts: ReactGridLayout.Layout[],
    readonly uiDispatch: React.Dispatch<UIAction>,
    readonly dataState: RitzDataState,
    readonly dataDispatch: ExtDispatch<RitzDataStateAction>,
    readonly appDataState: UserAppDataState,
    readonly appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    readonly loggedInAgent: Agent,
    readonly projectId: number,
    readonly homeId: number | undefined,
    readonly dashboardId: number,
    readonly trans: TFunction,
    readonly widgets: DashboardWidgetSettings[] | undefined
}) => {
    const layoutsByWidgetId = params.layouts.reduce((
        result: {
            [widgetId: string]: {
                [screenSize: string]: DashboardWidgetLayout | undefined
            } | undefined
        },
        layout: ReactGridLayout.Layout,
    ) => {
        return {
            ...result,
            [layout.i]: {
                lg: { x: layout.x, y: layout.y, w: layout.w, h: layout.h },
            },
        };
    }, {});

    // Check to see if any widget actually have been moved or resize. Sometime this function is called just because the user lift and then
    // drop the widget in the size place which mean the widget was not moved. If none of the widget was moved, don't need to make API call
    // to update the widgets.
    const widgetMoved = params.widgets && Object.entries(layoutsByWidgetId).find(([widgetId, newLayout]) => {
        // Find the widget object that has the same ID as the widgetId in the layoutsByWidgetId
        const widget = params.widgets?.find((w) => {
            return w.id.toString() === widgetId;
        });

        // If the widget exist AND if it's CURRENT layout is NOT the same as the NEW layout then that mean it's layout changed.
        // We can return true that we have found a widget the has been moved or resized and stop looking.
        return widget && widget.layouts && widget.layouts.lg && newLayout && newLayout.lg && (
            widget.layouts.lg.x !== newLayout.lg.x
            || widget.layouts.lg.y !== newLayout.lg.y
            || widget.layouts.lg.w !== newLayout.lg.w
            || widget.layouts.lg.h !== newLayout.lg.h
        );
    });

    // If can't find any widget that has different layout then that mean none of the widget moved. We can exist the function.
    if (!widgetMoved) {
        return;
    }

    const onCompleted = (result: DashboardLogicApiCallResponse) => {
        if (result.errorMessageKey) {
            params.uiDispatch(showErrorNotification(params.trans(result.errorMessageKey)));
        }
    };

    // Pass the new layout to updateWidgetsLayouts to save the widget's layout.
    if (params.homeId) {
        updateHomeWidgetsLayouts(params.appDataState, params.appDataDispatch, params.homeId, params.dashboardId, layoutsByWidgetId)
            .then(onCompleted);
    } else {
        updateProjectWidgetsLayouts(
            params.dataState, params.dataDispatch, params.projectId, params.loggedInAgent.id, params.dashboardId, layoutsByWidgetId,
        ).then(onCompleted);
    }
};



/**
 * Move a widget into a specific group. If widgetGroup widget is provided, this widget will be moved into that group. If widgetGroup is
 * not provided, this widget will be removed from the current group and will be placed in the outer dashboard
 */
export const moveWidgetToGroup = (params: {
    readonly targetWidget: DashboardWidgetSettings,
    readonly widgetGroup: WidgetGroupWidgetSettings | undefined,
    readonly uiDispatch: React.Dispatch<UIAction>,
    readonly dataState: RitzDataState,
    readonly dataDispatch: ExtDispatch<RitzDataStateAction>,
    readonly appDataState: UserAppDataState,
    readonly appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    readonly loggedInAgent: Agent,
    readonly projectId: number,
    readonly homeId: number | undefined,
    readonly dashboardId: number,
    readonly trans: TFunction,
}) => {
    const onCompleted = (result: DashboardLogicApiCallResponse) => {
        if (result.errorMessageKey) {
            params.uiDispatch(showErrorNotification(params.trans(result.errorMessageKey)));
        }
    };

    // Create a new widget by copy the targetWidget and replace the necessary attributes
    const updatedWidget: DashboardWidgetSettings = {
        ...params.targetWidget,
        layouts: {
            ...params.targetWidget.layouts,
            lg: {
                ...params.targetWidget.layouts.lg,
                x: null,    // Need to unset the widget's current position so that it get placed at the end of the container. We can't keep the
                y: null,    // widget's position because there might be a widget already at that same position.
            },
        },
        widgetGroupId: params.widgetGroup?.id,
    };

    if (params.homeId) {
        updateHomeDashboardWidget(params.appDataState, params.appDataDispatch, params.homeId, params.dashboardId, updatedWidget)
            .then(onCompleted);
    } else {
        updateProjectDashboardWidget(
            params.dataState, params.dataDispatch, params.projectId, params.loggedInAgent.id, params.dashboardId, updatedWidget,
        ).then(onCompleted);
    }
};



/**
 * Create the PRIMARY Actions to show in the widget's panel header, the top right corner. Most dashboard widget has the same options therefore we will
 * create a function to create these actions and reuse it
 */
export const createDashboardPrimaryWidgetActions = (params: {
    readonly isInMoveAndResizeMode: boolean,
    readonly trans: TFunction,
    readonly viewDataHandler: ()=> void,
}): readonly BaseCompAction[] | undefined => {
    if (!params.isInMoveAndResizeMode) {
        return [{
            icon   : 'visibility',
            color  : 'primary',
            onClick: params.viewDataHandler,
        }];
    }

    return undefined;
};



/**
 * Create the SECONDARY Actions to show in the widget's panel header, the top right corner. Most dashboard widget has the same options therefore
 * we will create a function to create these actions and reuse it
 */
export const createDashboardSecondaryWidgetActions = (params: {
    readonly targetWidget: DashboardWidgetSettings,
    readonly widgetGroupWidgets: readonly WidgetGroupWidgetSettings[] | undefined,
    readonly isInMoveAndResizeMode: boolean,
    readonly uiDispatch: React.Dispatch<UIAction>,
    readonly dataState: RitzDataState,
    readonly dataDispatch: ExtDispatch<RitzDataStateAction>,
    readonly appDataState: UserAppDataState,
    readonly appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    readonly loggedInAgent: Agent,
    readonly projectId: number,
    readonly homeId: number | undefined,
    readonly dashboardId: number,
    readonly trans: TFunction,
    readonly showDashboardWidgetPreviewHandler: ()=> void,
    readonly fetchDataHandler: ()=> void,
    readonly enableMoveAndResizeModeHandler?: ()=> void;
}): readonly (BaseCompAction | BaseCompSubAction)[] | undefined => {
    if (!params.isInMoveAndResizeMode) {
        const groupingActions: (BaseCompAction | BaseCompSubAction)[] = [];

        // If there are 1 or more groups, add options to move this widget to these groups
        if (params.widgetGroupWidgets && params.widgetGroupWidgets.length > 0) {
            groupingActions.push({
                icon   : 'fas fa-object-group',
                label  : capitalizeAll(params.trans('dashboard.action.move_to_group')),
                actions: params.widgetGroupWidgets.map((widgetGroup) => {
                    return {
                        label   : widgetGroup.title,
                        icon    : widgetGroup.icon,
                        disabled: widgetGroup.id === params.targetWidget.widgetGroupId,
                        onClick : () => {
                            moveWidgetToGroup({
                                targetWidget   : params.targetWidget,
                                widgetGroup,
                                uiDispatch     : params.uiDispatch,
                                dataState      : params.dataState,
                                dataDispatch   : params.dataDispatch,
                                appDataState   : params.appDataState,
                                appDataDispatch: params.appDataDispatch,
                                loggedInAgent  : params.loggedInAgent,
                                projectId      : params.projectId,
                                homeId         : params.homeId,
                                dashboardId    : params.dashboardId,
                                trans          : params.trans,
                            });
                        },
                    };
                }),
            });
        }

        // If this widget is already in a group, add an option to remove it from the group
        if (params.targetWidget.widgetGroupId) {
            groupingActions.push({
                icon   : 'fas fa-object-ungroup',
                label  : capitalizeAll(params.trans('dashboard.action.remove_from_group')),
                onClick: () => {
                    moveWidgetToGroup({
                        targetWidget   : params.targetWidget,
                        widgetGroup    : undefined,
                        uiDispatch     : params.uiDispatch,
                        dataState      : params.dataState,
                        dataDispatch   : params.dataDispatch,
                        appDataState   : params.appDataState,
                        appDataDispatch: params.appDataDispatch,
                        loggedInAgent  : params.loggedInAgent,
                        projectId      : params.projectId,
                        homeId         : params.homeId,
                        dashboardId    : params.dashboardId,
                        trans          : params.trans,
                    });
                },
            });
        }


        return [{
            icon   : 'settings',
            label  : capitalizeAll(params.trans('label.settings')),
            onClick: params.showDashboardWidgetPreviewHandler,
        }, {
            icon   : 'replay',
            label  : capitalizeAll(params.trans('label.reload')),
            onClick: params.fetchDataHandler,
        }, ...groupingActions,
        {
            icon   : 'open_with',
            label  : capitalizeAll(params.trans('dashboard.action.move_and_resize_widgets')),
            onClick: () => {
                if (params.enableMoveAndResizeModeHandler) {
                    params.enableMoveAndResizeModeHandler();
                }
            },
        }];
    }

    return undefined;
};



/**
 * Get the list of SELECTED metrics' names from the data source
 */
export const getDataSourceSelectedMetricNames = (
    dataSource: TSDBDashboardWidgetDataSource | undefined,
): readonly string[] => {
    // for SIMPLE series, the list of selected metric names is the dataSource.seriesIds
    if (isSimpleSeriesDashboardWidgetDataSource(dataSource)) {
        return dataSource.seriesIds;
    }
    // Both COLLECTION and ENTITY selected metric names are stored in the dataSource.valueNames and tagNames
    if (isCollectionDashboardWidgetDataSource(dataSource) || isEntityDashboardWidgetDataSource(dataSource)) {
        return [...(dataSource.valueNames || []), ...(dataSource.tagNames || [])];
    }

    return [];
};


/**
 * Initialize the BASE dashboard widget inputs
 * @param widget
 * @returns
 */
export const initializeBaseDashboardWidgetUserInputs = (
    widget?: DashboardWidgetSettings | undefined,
): DashboardWidgetInputs => {
    return {
        title: widget?.title,
        icon : widget?.icon,
    };
};


/**
 * Initialize the DATA dashboard inputs. Because DataDashboardWidget extends base DashboardWidgetSettings, it will call
 * initializeBaseDashboardWidgetUserInputs to initialize the inputs for the base dashboard widget settings
 * @param widget
 * @returns
 */
export const initializeDataDashboardWidgetUserInputs = (
    widget?: DataDashboardWidgetSettings | undefined,
): DataDashboardWidgetInputs => {
    return {
        ...initializeBaseDashboardWidgetUserInputs(widget),

        // Initialize other inputs for DataDashboardWidgetSettings
        reloadDataFreq: widget?.reloadDataFreqInMins ?? 0,
    };
};


/**
 * Initialize the dataSource inputs based on the provided dataSource object and other params.
 * @param dataSource
 * @param entity
 * @param metricsDefinition
 * @param homeId
 * @returns
 */
export const initializeTSDBDashboardWidgetDataSourceUserInputs = (
    dataSource: TSDBDashboardWidgetDataSource | undefined,
    entityFeatureEnabled: boolean | undefined,
    entity: Entity | undefined,
    metricsDefinition: EntityMetricsDefinition | undefined,
    homeId: number | undefined,
): TSDBDashboardWidgetDataSourceInputs => {

    // Get the names of the selected metrics, the selected valueNames, and/or tagNames
    const selectedMetricNames = getDataSourceSelectedMetricNames(dataSource);

    /**
     * Create a metricMetadata for each metricName e.g. seriesId or valueNames, or tagName. This block of code will create this object
     *      {
     *          temperature: {
     *              displayName: '...', unit: '...'
     *          },
     *          pressure: {
     *              displayName: '...'
     *          },
     *          humidity: {
     *          },
     *          ...
     *      }
     */
    const dataSourceMetricsMetadata = selectedMetricNames.reduce((
        result: MetricsMetadata, metricName,
    ) => {
        if (!result[metricName]) {
            return {
                ...result,
                [metricName]: {
                },
            };
        }
        return result;
    }, dataSource?.metricsMetadata || {});


    return {
        homeId        : dataSource?.homeId ?? homeId,
        // TODO - Remove 'timeSeriesType' check later once none of the project use old dashboard widget settings
        dataSourceType: (dataSource?.dataSourceType || dataSource?.timeSeriesType)
                            ?? (entityFeatureEnabled ? TSDBDataSourceType.ENTITY : TSDBDataSourceType.COLLECTION),
        smartModuleId: isSimpleSeriesDashboardWidgetDataSource(dataSource) || isCollectionDashboardWidgetDataSource(dataSource)
            ? dataSource.smartModuleId : undefined,
        aggregationType  : dataSource?.aggregationType ?? TimeSeriesAggregation.AVG,
        intervalType     : dataSource?.intervalType ?? AggregatedDataIntervalType.DAILY,
        seriesIds        : isSimpleSeriesDashboardWidgetDataSource(dataSource) ? dataSource.seriesIds : undefined,
        collectionId     : isCollectionDashboardWidgetDataSource(dataSource) ? dataSource.collectionId : undefined,
        entity           : isEntityDashboardWidgetDataSource(dataSource) ? entity : undefined,
        metricsDefinition: isEntityDashboardWidgetDataSource(dataSource) ? metricsDefinition : undefined,

        // COLLECTION and ENTITY data sources use valueNames and tagNames
        valueNames: isCollectionDashboardWidgetDataSource(dataSource) || isEntityDashboardWidgetDataSource(dataSource)
            ? dataSource.valueNames : undefined,
        tagNames: isCollectionDashboardWidgetDataSource(dataSource) || isEntityDashboardWidgetDataSource(dataSource)
            ? dataSource.tagNames : undefined,

        metricsMetadata: dataSourceMetricsMetadata,
    };
};


/**
 * Initialize widget's BASE VIEW inputs. This will only initialize the 'viewMetricsMetadata'. Each specific type of dashboard
 * will have other inputs that can not be initialize by this function.
 * @param widget
 * @param existingMetricsMetadata
 * @param metricNames
 * @returns
 */
export const initializeDashboardWidgetBaseViewUserInputs = (
    existingMetricsMetadata: MetricsMetadata | undefined,
    metricNames: readonly string[],
): DashboardWidgetViewSettingsInputs => {

    /**
     * Create a metricMetadata for each metricName e.g. seriesId or valueNames, or tagName. This block of code will create this object
     *      {
     *          temperature: {
     *              displayName: '...', unit: '...'
     *          },
     *          pressure: {
     *              displayName: '...'
     *          },
     *          humidity: {
     *          },
     *          ...
     *      }
     */
    const viewMetricsMetadata = metricNames.reduce((
        result: MetricsMetadata, metricName,
    ) => {
        if (!result[metricName]) {
            return {
                ...result,
                [metricName]: {
                },
            };
        }
        return result;
    }, existingMetricsMetadata || {});

    return {
        viewMetricsMetadata,
    };
};



/**
 * Custom hook to create input fields for widgets that use TSDB data source.
 *
 * @param projectId
 * @param homeId
 * @param widget
 * @param includeTagValuesInput
 * @param supportedIntervalTypes
 * @param currentUserInputs
 * @param inputErrors
 * @param trans
 * @returns
 */
export const useCreateTSDBDashboardWidgetSettingsCompFields = (
    projectId: number,
    homeId: number | undefined,
    widget: DashboardWidgetSettings | undefined,
    includeTagValuesInput: boolean,
    supportedIntervalTypes: readonly (AggregatedDataIntervalType | RawDataIntervalType)[],
    currentUserInputs: TSDBDashboardWidgetDataSourceInputs,
    inputErrors: DashboardWidgetInputErrors & TSDBDashboardWidgetDataSourceInputErrors,
    entityClasses: readonly EntityClass[] | undefined,
    trans: TFunction,
): BaseDashboardWidgetSettingsCompFields & DataDashboardWidgetSettingsCompFields & TSDBDashboardWidgetSettingsFields => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;


    // Get all Project's homes
    const allHomes = useGetAllProjectHomes(projectId);

    // Get all the data sources based on the current user inputs
    const { smartModules, allHomeTimeSeries, allHomeCollections, allMetricsDefinitions } = useGetWidgetDataSources(
        currentUserInputs, projectId, entityClasses,
    );

    // Get the list of supported data source types.
    const supportedDataSourceTypes = useGetSupportedDataSourceTypes(project, loggedInAgent);

    /**
     * Drop down options to be used in the Home ID input field
     */
    const homeInputOptions = useMemo((): readonly SelectInputOption<number>[] => {
        const options = createHomeOptions(allHomes);
        return options;
    }, [allHomes]);


    /**
     * Drop down options to be used in the Data Source Type input field
     */
    const tsdbDataSourceTypeInputOptions = useMemo((): readonly SelectInputOption<TSDBDataSourceType>[] => {
        const options = createTSDBDataSourceTypeInputOptions(supportedDataSourceTypes, trans);
        return options;
    }, [supportedDataSourceTypes, trans]);


    /**
     * Drop down options to be used in the Icon input field
     */
    const iconInputOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createIconOptions(true, capitalizeAll(trans('label.none')));
    }, [trans]);


    /**
     * Drop down options to be used in the Reload Data Frequency input field
     */
    const reloadDataFreqInputOptions = useMemo((): readonly SelectInputOption<number>[] => {
        return createReloadDataOptions(trans);
    }, [trans]);


    /**
     * Drop down options to be used in the Smart Module ID input field
     */
    const smartModuleInputOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createSmartModuleInputOptions(smartModules);
    }, [smartModules]);


    /**
     * Drop down options to be used in the seriesIds input field
     */
    const timeSeriesOptions = useMemo((): readonly SelectInputOption<string> [] => {
        return createTimeSeriesOptions(allHomeTimeSeries);
    }, [allHomeTimeSeries]);


    /**
     * Drop down options to be used in the collectionId input field
     */
    const collectionOptions = useMemo((): readonly SelectInputOption<string> [] => {
        return createCollectionOptions(allHomeCollections);
    }, [allHomeCollections]);


    /**
     * Drop down options to be used in the metricsDefinition input field
     */
    const metricsDefinitionOptions = useMemo((): readonly SelectInputOption<EntityMetricsDefinition> [] => {
        return createMetricsDefinitionOptions(allMetricsDefinitions);
    }, [allMetricsDefinitions]);


    /**
     * Drop down options to be used in the View Type input field
     */
    const aggregationTypeOptions = useMemo((): readonly SelectInputOption<TimeSeriesAggregation | TypeNone> [] => {
        return createAggregationTypeOptions(trans, currentUserInputs);
    }, [currentUserInputs, trans]);


    /**
     * Drop down options to be used in the View Type input field. Because different widget type support different options, these
     * options must be provided by the caller.
     */
    const intervalTypeOptions = useMemo((): readonly SelectInputOption<AggregatedDataIntervalType | RawDataIntervalType> [] => {
        return createIntervalTypeOptions(supportedIntervalTypes, trans, currentUserInputs);
    }, [currentUserInputs, supportedIntervalTypes, trans]);


    /**
     * Drop down options to be used in the valueNames input field
     * Drop down options to be used in the tagNames input field
     * These 2 set of options need to be re-created each time the user select a different collectionId or entity and metricsDefinition so we put
     * them inside 1 block and do both at the same time
     */
    const [collectionValueNamesOptions, collectionTagNamesOptions] = useMemo((): [
        readonly SelectInputOption<string> [],
        readonly SelectInputOption<string> []
    ] => {
        if (currentUserInputs.dataSourceType === TSDBDataSourceType.COLLECTION && currentUserInputs.collectionId) {
            return [
                createCollectionValueNameOptions(allHomeCollections, currentUserInputs.collectionId),
                createCollectionTagNameOptions(allHomeCollections, currentUserInputs.collectionId),
            ];
        }
        if (currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY && currentUserInputs.entity && currentUserInputs.metricsDefinition) {
            return [
                createCollectionMetricNameOptionsFromMetricInfo(currentUserInputs.metricsDefinition.numericMetrics),
                createCollectionMetricNameOptionsFromMetricInfo(currentUserInputs.metricsDefinition.tagMetrics),
            ];
        }
        return [
            [], [],
        ];
    }, [currentUserInputs.dataSourceType, currentUserInputs.collectionId, currentUserInputs.entity, currentUserInputs.metricsDefinition,
        allHomeCollections]);


    /**
     * Create the set of fields for the widget settings
     */
    const dashboardWidgetCompFields = useMemo(():
    BaseDashboardWidgetSettingsCompFields & DataDashboardWidgetSettingsCompFields & TSDBDashboardWidgetSettingsFields => {

        // Some field can only be visible based on another field. For example, the 'Series IDs' field should only be visible if the user chose
        // 'Simple' for 'Time Series Type' field. And 'Collection ID' option is only visible of the user chose 'Collection' for the
        // 'Time Series Type' field. So we need determine which field can be shown/hidden based on the current input
        const showEntityField = currentUserInputs.homeId !== undefined && currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY;
        const showMetricsDefinitionField = showEntityField && currentUserInputs.entity !== undefined;

        const showTimeSeriesIdsField = currentUserInputs.smartModuleId
                                        && currentUserInputs.dataSourceType === TSDBDataSourceType.SIMPLE;
        const showCollectionIdField = currentUserInputs.smartModuleId
                                        && currentUserInputs.dataSourceType === TSDBDataSourceType.COLLECTION;
        const showValueNamesField = (showCollectionIdField && currentUserInputs.collectionId)
        || (showEntityField && showMetricsDefinitionField && currentUserInputs.metricsDefinition);
        const showTagNamesField = (showCollectionIdField && currentUserInputs.collectionId)
        || (showEntityField && showMetricsDefinitionField && currentUserInputs.metricsDefinition);
        const metricsSelected = (currentUserInputs.seriesIds && currentUserInputs.seriesIds.length > 0)
            || (currentUserInputs.valueNames && currentUserInputs.valueNames.length > 0)
            || (currentUserInputs.tagNames && currentUserInputs.tagNames.length > 0);


        return {
            title: {
                label   : capitalizeAll(trans('label.title')),
                value   : widget ? widget.title : '',
                editable: true,
                required: true,
                error   : inputErrors.title ? trans(inputErrors.title) : undefined,
            },
            icon: {
                label   : capitalizeAll(trans('label.icon')),
                value   : widget ? widget.icon : undefined,
                options : iconInputOptions,
                editable: true,
                error   : inputErrors.icon ? trans(inputErrors.icon) : undefined,
            },
            reloadDataFreq: {
                label         : capitalizeAll(trans('label.reload_data_freq')),
                value         : currentUserInputs.reloadDataFreq,
                options       : reloadDataFreqInputOptions,
                noOptionsLabel: capitalizeAll(trans('label.no_options')),
                editable      : true,
                required      : true,
                error         : inputErrors.reloadDataFreq ? trans(inputErrors.reloadDataFreq) : undefined,
            },
            dataSource: {
                homeId: {
                    label   : capitalizeAll(trans('label.home_id')),
                    value   : currentUserInputs.homeId,
                    editable: homeId === undefined,
                    required: true,
                    options : homeInputOptions,
                    hidden  : homeId !== undefined,
                    error   : inputErrors.homeId ? trans(inputErrors.homeId) : undefined,
                },
                dataSourceType: {
                    label   : capitalizeAll(trans('label.data_source_type')),
                    value   : currentUserInputs.dataSourceType,
                    editable: true,
                    required: true,
                    options : tsdbDataSourceTypeInputOptions,
                    hidden  : currentUserInputs.homeId === undefined,
                    error   : inputErrors.dataSourceType ? trans(inputErrors.dataSourceType) : undefined,
                },

                // Fields for SIMPLE and COLLECTION data source type
                smartModuleId: currentUserInputs.dataSourceType !== TSDBDataSourceType.ENTITY ? {
                    label         : capitalizeAll(trans('label.tsdb_smart_module_id')),
                    value         : currentUserInputs.smartModuleId || '',
                    options       : smartModuleInputOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    required      : true,
                    hidden        : currentUserInputs.homeId === undefined,
                    error         : inputErrors.smartModuleId ? trans(inputErrors.smartModuleId) : undefined,
                } : undefined,
                seriesIds: currentUserInputs.dataSourceType !== TSDBDataSourceType.ENTITY ? {
                    label                        : capitalizeAll(trans('label.series_ids')),
                    value                        : currentUserInputs.seriesIds || [],
                    multiple                     : true,
                    options                      : timeSeriesOptions,
                    multipleSelectionDisplayLimit: 3,
                    noOptionsLabel               : capitalizeAll(trans('label.no_options')),
                    editable                     : true,
                    hidden                       : !showTimeSeriesIdsField,
                    error                        : inputErrors.seriesIds ? trans(inputErrors.seriesIds) : undefined,
                } : undefined,
                collectionId: currentUserInputs.dataSourceType !== TSDBDataSourceType.ENTITY ? {
                    label         : capitalizeAll(trans('label.collection_id')),
                    value         : currentUserInputs.collectionId || '',
                    options       : collectionOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    hidden        : !showCollectionIdField,
                    error         : inputErrors.collectionId ? trans(inputErrors.collectionId) : undefined,
                } : undefined,

                // Fields for ENTITY data source type
                entity: currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY ? {
                    label                        : capitalizeAll(trans('field.entity.label')),
                    value                        : currentUserInputs.entity,
                    editable                     : true,
                    required                     : true,
                    hidden                       : !showEntityField,
                    error                        : inputErrors.entity ? trans(inputErrors.entity) : undefined,
                    entitiesListingContainerProps: {
                        projectId,
                        homeId: currentUserInputs.homeId,
                    },
                    texts: {
                        clearButtonLabel : capitalizeAll(trans('label.clear')),
                        cancelButtonLabel: capitalizeAll(trans('label.cancel')),
                        selectButtonLabel: capitalizeAll(trans('label.select')),
                        emptyValueDisplay: capitalizeAll(trans('label.none')),
                    },
                } : undefined,
                metricsDefinition: currentUserInputs.dataSourceType === TSDBDataSourceType.ENTITY ? {
                    label         : capitalizeAll(trans('field.metrics_definition_id.label')),
                    value         : currentUserInputs.metricsDefinition,
                    options       : metricsDefinitionOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    required      : true,
                    hidden        : !showMetricsDefinitionField,
                    error         : inputErrors.metricsDefinition ? trans(inputErrors.metricsDefinition) : undefined,
                } : undefined,

                // Fields for ALL data source type
                valueNames: {
                    label                        : capitalizeAll(trans('label.value_names')),
                    value                        : currentUserInputs.valueNames || [],
                    multiple                     : true,
                    options                      : collectionValueNamesOptions,
                    multipleSelectionDisplayLimit: 5,
                    noOptionsLabel               : capitalizeAll(trans('label.no_options')),
                    editable                     : true,
                    hidden                       : !showValueNamesField,
                    error                        : inputErrors.valueNames ? trans(inputErrors.valueNames) : undefined,
                },
                tagNames: includeTagValuesInput ? {
                    label                        : capitalizeAll(trans('label.tag_names')),
                    value                        : currentUserInputs.tagNames || [],
                    multiple                     : true,
                    options                      : collectionTagNamesOptions,
                    multipleSelectionDisplayLimit: 5,
                    noOptionsLabel               : capitalizeAll(trans('label.no_options')),
                    editable                     : true,
                    hidden                       : !showTagNamesField,
                    error                        : inputErrors.tagNames ? trans(inputErrors.tagNames) : undefined,
                } : undefined,
                metricsMetadata: {
                    label   : 'metadata',
                    value   : currentUserInputs.metricsMetadata,
                    editable: true,
                    hidden  : !metricsSelected,
                    variant : 'outlined',
                    error   : inputErrors.metricsMetadata
                        ? JSON.stringify(Object.keys(inputErrors.metricsMetadata).reduce((result, metricName) => {
                            // translate the error message for each metric
                            const metricErrorMessage = inputErrors.metricsMetadata?.[metricName];
                            if (metricErrorMessage) {
                                return {
                                    ...result,
                                    [metricName]: trans(metricErrorMessage),
                                };
                            }
                            return result;
                        }, {}))                 // 'error' is a string therefore we need to JSON.stringify the metricMetadata error object
                        : undefined,
                },
                aggregationType: {
                    label         : capitalizeAll(trans('label.time_series_aggregation_type')),
                    value         : currentUserInputs.aggregationType,
                    options       : aggregationTypeOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    hidden        : !metricsSelected,
                    error         : inputErrors.aggregationType ? trans(inputErrors.aggregationType) : undefined,
                },
                intervalType: {
                    label         : capitalizeAll(trans('dashboard.labels.data_interval_type')),
                    value         : currentUserInputs.intervalType,
                    options       : intervalTypeOptions,
                    noOptionsLabel: capitalizeAll(trans('label.no_options')),
                    editable      : true,
                    hidden        : !metricsSelected,
                    error         : inputErrors.intervalType ? trans(inputErrors.intervalType) : undefined,
                },
            },
        };
    }, [aggregationTypeOptions, collectionOptions, collectionTagNamesOptions, collectionValueNamesOptions, currentUserInputs.aggregationType,
        currentUserInputs.collectionId, currentUserInputs.dataSourceType, currentUserInputs.entity, currentUserInputs.homeId,
        currentUserInputs.intervalType, currentUserInputs.metricsDefinition, currentUserInputs.metricsMetadata, currentUserInputs.reloadDataFreq,
        currentUserInputs.seriesIds, currentUserInputs.smartModuleId, currentUserInputs.tagNames, currentUserInputs.valueNames,
        includeTagValuesInput, homeId, homeInputOptions, iconInputOptions, inputErrors.aggregationType, inputErrors.collectionId,
        inputErrors.dataSourceType, inputErrors.entity, inputErrors.homeId, inputErrors.icon, inputErrors.intervalType,
        inputErrors.metricsDefinition, inputErrors.metricsMetadata, inputErrors.reloadDataFreq, inputErrors.seriesIds,
        inputErrors.smartModuleId, inputErrors.tagNames, inputErrors.title, inputErrors.valueNames, intervalTypeOptions,
        metricsDefinitionOptions, projectId, reloadDataFreqInputOptions, smartModuleInputOptions, timeSeriesOptions, trans,
        tsdbDataSourceTypeInputOptions, widget]);

    return dashboardWidgetCompFields;
};


/**
 */
export const getTSDashboardWidgetSettingsCompTexts = (trans: TFunction): TSDBDashboardWidgetSettingsCompTexts => {
    return {
        dataSourceSettings   : capitalizeAll(trans('dashboard.labels.data_source_settings')),
        viewSettings         : capitalizeAll(trans('dashboard.labels.view_settings')),
        metricsMetadataLabels: {
            labels: {
                displayName               : capitalizeAll(trans('label.display_name')),
                unit                      : capitalizeAll(trans('label.unit')),
                lowValueWarningThreshold  : capitalizeAll(trans('dashboard.label.low_value_warning_threshold')),
                lowValueCriticalThreshold : capitalizeAll(trans('dashboard.label.low_value_critical_threshold')),
                highValueWarningThreshold : capitalizeAll(trans('dashboard.label.high_value_warning_threshold')),
                highValueCriticalThreshold: capitalizeAll(trans('dashboard.label.high_value_critical_threshold')),
                decimalPlaces             : capitalizeAll(trans('dashboard.label.decimal_places')),
                conversionFormula         : capitalizeAll(trans('dashboard.label.conversion_formula')),
            },
            descriptions: {
                conversionFormula: capitalizeFirst(trans('dashboard.description.conversion_formula')),
            },
        },
    };
};
